import * as React from 'react';
import {useState} from 'react';

import memoize from 'memoize-one';
import {
  latestMedia,
  mediaSrc,
  runFileSource,
  memoizedStepsWithKey,
  labelComponent,
} from '../util/media';
import {runLink} from '../util/runhelpers';
import MessageMediaNotFound from './MessageMediaNotFound';
import {MediaCardProps} from './MediaCard';
import {RunHistoryRow} from '../types/run';
import makeComp from '../util/profiler';
import HtmlViewer from './Panel2/HtmlViewer';

// a single image card
const HtmlCard = makeComp(
  (props: MediaCardProps) => {
    const {run, globalStep, mediaKey, mediaIndex, runSignature, height} = props;

    const stepsWithMedia = memoizedStepsWithKey(run, mediaKey);

    const [currentStep, setCurrentStep] = useState<number | undefined>();
    const [baseStep, setBaseStep] = useState<number>();

    // We want to set a base step and a current step
    // - current step can be rolled back locally
    // - base step is controlled by the caller
    if (props.globalStep !== baseStep) {
      const latestStepData = latestMedia(stepsWithMedia, props.globalStep);
      setBaseStep(props.globalStep);
      if (latestStepData != null) {
        setCurrentStep(latestStepData._step);
      }
    }
    const currentStepData = stepsWithMedia.find(v => v._step === currentStep);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const media = getMedia(currentStepData, mediaKey, mediaIndex);

    let src: string;

    if (media && media.path) {
      src = runFileSource(runSignature, media.path);
    } else {
      src = mediaSrc(
        runSignature,
        [mediaKey, currentStep, mediaIndex] as string[],
        'html'
      );
    }

    return (
      <HtmlViewer
        headerElements={labelComponent(props, currentStep, titleLink)}
        notFoundElements={[
          <MessageMediaNotFound
            mediaIndex={mediaIndex}
            mediaKey={mediaKey}
            stepIndex={globalStep}
            mediaType="html"
          />,
        ]}
        htmlNotFound={currentStep == null}
        height={height}
        src={src}
      />
    );
  },
  {id: 'HtmlCard'}
);

export default HtmlCard;

// Get the Media object logged for the given key and step.
const getMedia = memoize(
  (stepData: RunHistoryRow | undefined, mediaKey: string, step: number) => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'html') {
      if (stepMedia.html) {
        return stepMedia.html[step];
      } else {
        return null;
      }
    } else if (stepMedia._type === 'html-file') {
      return stepMedia;
    } else {
      return null;
    }
  }
);

// Get the new-style Media object or null if there isn't one.
